import React from "react";
import { Clock, MapPin } from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// local YYYY-MM-DD key (no timezone shift)
export const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const fmtTime = (hhmm) => {
  if (!hhmm || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return hhmm;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
};

export const next7Days = (start = new Date()) => {
  const base = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return d;
  });
};

export const PickupHoursCard = ({ settings }) => {
  const pickupHours = settings?.pickup_hours || {};
  const locationLabel = settings?.opening_soon_location_label || "Drive-through purchase & pickup";
  const location = settings?.opening_soon_location || "";
  const days = next7Days();
  const todayKey = dateKey(new Date());

  const renderHours = (entry) => {
    if (!entry) return <span className="text-slate-400 italic">Hours not posted yet</span>;
    if (entry.closed) return <span className="text-slate-400 font-bold">Closed</span>;
    const wins = (entry.windows || []).filter((w) => w.start && w.end);
    if (wins.length === 0) return <span className="text-slate-400 italic">Hours not posted yet</span>;
    return (
      <span className="font-bold text-brand-navy">
        {wins.map((w, i) => (
          <span key={i}>
            {i > 0 && <span className="text-slate-400 mx-1.5">·</span>}
            {fmtTime(w.start)} – {fmtTime(w.end)}
          </span>
        ))}
      </span>
    );
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="pickup-hours-card">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="bg-brand-navy text-white p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 bg-brand-yellow text-brand-navy px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest">
            <Clock className="h-3.5 w-3.5" /> Pickup hours this week
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl mt-3">When you can pick up</h2>
          {location && (
            <div className="mt-2 flex items-start gap-2 text-white/85 text-sm">
              <MapPin className="h-4 w-4 mt-0.5 text-brand-yellow shrink-0" />
              <span>{locationLabel ? `${locationLabel} · ` : ""}{location}</span>
            </div>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {days.map((d) => {
            const key = dateKey(d);
            const isToday = key === todayKey;
            const entry = pickupHours[key];
            return (
              <div
                key={key}
                data-testid={`pickup-row-${key}`}
                className={`flex items-center justify-between gap-4 px-5 sm:px-8 py-3.5 ${isToday ? "bg-brand-yellowLight" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`font-heading text-base ${isToday ? "text-brand-red" : "text-brand-navy"}`}>
                    {WEEKDAYS[d.getDay()]}, {MONTHS[d.getMonth()]} {d.getDate()}
                  </div>
                  {isToday && <span className="text-[10px] font-black uppercase tracking-widest bg-brand-red text-white px-2 py-0.5 rounded-full">Today</span>}
                </div>
                <div className="text-sm text-right">{renderHours(entry)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
